import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { stripeService } from '../../services/stripe';
import SubscriptionGate from '../../components/ui/SubscriptionGate';
import propertyService from '../../services/propertyService';
import {
  entitiesService, assetsService, debtsService, revenuesService, planEventsService,
  scenariosService,
  ensureDefaultEntities, ensureDefaultAssets, migrateLocalStorageToSupabase, isSupabaseReady
} from '../../services/investmentPlanningService';
import { generate25YearProjection } from '../../services/investmentProjectionEngine';
import EntitiesManager from './components/EntitiesManager';
import AssetsManager from './components/AssetsManager';
import DebtsManager from './components/DebtsManager';
import RevenuesManager from './components/RevenuesManager';
import RevenuesPlanningTab from './components/RevenuesPlanningTab';
import BilanSummary from './components/BilanSummary';
import PlanningEvents from './components/PlanningEvents';
import CashflowDirectionInitial from './components/CashflowDirectionInitial';
import AnalysisTab from './components/analyse/AnalysisTab';
import PlanningHorizonControls from './components/PlanningHorizonControls';
import { mergeAssetsForDebug } from '../../services/planningTax/assetDebugSnapshot';

const CURRENT_YEAR = new Date().getFullYear();

const BilanSection = ({ id, title, icon, collapsed, onToggle, children }) => {
  return (
    <div className="bg-card border border-border rounded-xl" data-testid={`bilan-section-${id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-5 md:px-6 py-4"
        data-testid={`bilan-section-toggle-${id}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {icon && <Icon name={icon} size={18} className="text-primary shrink-0" />}
          <span className="text-sm md:text-base font-semibold text-foreground truncate" style={{ fontFamily: 'var(--font-heading)' }}>
            {title}
          </span>
        </div>
        <Icon name={collapsed ? 'ChevronDown' : 'ChevronUp'} size={18} className="text-muted-foreground shrink-0" />
      </button>
      {!collapsed && <div className="px-5 md:px-6 pb-5 md:pb-6">{children}</div>}
    </div>
  );
};

const InvestmentPlanning = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { formatCurrency } = useCurrency();
  const [activeTab, setActiveTab] = useState('bilan');
  const currentTier = stripeService.normalizeTier(userProfile?.subscription_tier);
  const isPro = currentTier === 'pro';
  const [showGate, setShowGate] = useState(false);

  // Data state
  const [entities, setEntities] = useState([]);
  const [assets, setAssets] = useState([]);
  const [debts, setDebts] = useState([]);
  const [revenues, setRevenues] = useState([]);
  const [planEvents, setPlanEvents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storageMode, setStorageMode] = useState(''); // 'supabase' or 'localStorage'

  // Bilan UI: collapsible sections
  const [bilanCollapsed, setBilanCollapsed] = useState({
    summary: false,
    cashflow: false,
    entities: false,
    assets: false,
    debts: true,
    revenues: true,
  });

  // Scenario state
  const [scenarios, setScenarios] = useState([]);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [analysisScenarioA, setAnalysisScenarioA] = useState('__current__');
  const [analysisScenarioB, setAnalysisScenarioB] = useState('__current__');

  const [planningHorizonYears, setPlanningHorizonYears] = useState(25);
  const [planningDeathYear, setPlanningDeathYear] = useState(() => CURRENT_YEAR + 25);

  const uid = user?.id;

  const enabledPlanEvents = useMemo(
    () => planEvents.filter(e => !e.details?.disabled),
    [planEvents],
  );

  const hasPlanningData = assets.length > 0 || debts.length > 0 || revenues.length > 0;

  const planningProjection = useMemo(() => {
    if (!hasPlanningData) {
      return {
        data: [],
        assetNames: [],
        revenueNames: [],
        deathSummary: null,
        cashInvestments: [],
        horizonYears: planningHorizonYears,
        deathScenarioYear: planningDeathYear,
      };
    }
    return generate25YearProjection(assets, debts, revenues, enabledPlanEvents, entities, {
      horizonYears: planningHorizonYears,
      deathScenarioYear: planningDeathYear,
    });
  }, [
    assets,
    debts,
    revenues,
    enabledPlanEvents,
    entities,
    hasPlanningData,
    planningHorizonYears,
    planningDeathYear,
  ]);

  const buildProjectionFromSnapshot = useCallback((snapshot) => {
    if (!snapshot) return { data: [] };
    return generate25YearProjection(
      snapshot.assets || [],
      snapshot.debts || [],
      snapshot.revenues || [],
      (snapshot.events || []).filter(e => !e.details?.disabled),
      snapshot.entities || [],
      { horizonYears: planningHorizonYears, deathScenarioYear: planningDeathYear },
    );
  }, [planningDeathYear, planningHorizonYears]);

  const getScenarioSnapshot = useCallback((scenarioId) => {
    if (scenarioId === '__current__') {
      return { entities, assets, debts, revenues, events: planEvents };
    }
    const sc = scenarios.find(s => s.id === scenarioId);
    return sc?.data || null;
  }, [assets, debts, entities, planEvents, revenues, scenarios]);

  const analysisProjectionA = useMemo(
    () => buildProjectionFromSnapshot(getScenarioSnapshot(analysisScenarioA)),
    [analysisScenarioA, buildProjectionFromSnapshot, getScenarioSnapshot],
  );
  const analysisProjectionB = useMemo(
    () => buildProjectionFromSnapshot(getScenarioSnapshot(analysisScenarioB)),
    [analysisScenarioB, buildProjectionFromSnapshot, getScenarioSnapshot],
  );

  const buildDebugAssetsForScenario = useCallback(
    (scenarioId, projection) => {
      const snapshot = getScenarioSnapshot(scenarioId);
      const bilanAssets = snapshot?.assets || [];
      const catalog = projection?.projectedAssetCatalog || [];
      return mergeAssetsForDebug(bilanAssets, catalog);
    },
    [getScenarioSnapshot],
  );

  const analysisDebugAssetsA = useMemo(
    () => buildDebugAssetsForScenario(analysisScenarioA, analysisProjectionA),
    [analysisScenarioA, analysisProjectionA, buildDebugAssetsForScenario],
  );
  const analysisDebugAssetsB = useMemo(
    () => buildDebugAssetsForScenario(analysisScenarioB, analysisProjectionB),
    [analysisScenarioB, analysisProjectionB, buildDebugAssetsForScenario],
  );

  const analysisEntitiesA = useMemo(
    () => getScenarioSnapshot(analysisScenarioA)?.entities || [],
    [analysisScenarioA, getScenarioSnapshot],
  );
  const analysisEntitiesB = useMemo(
    () => getScenarioSnapshot(analysisScenarioB)?.entities || [],
    [analysisScenarioB, getScenarioSnapshot],
  );

  useEffect(() => {
    const maxCal = CURRENT_YEAR + planningHorizonYears;
    setPlanningDeathYear((y) => {
      const n = typeof y === 'number' ? y : parseInt(y, 10);
      if (!Number.isFinite(n)) return maxCal;
      return Math.min(Math.max(n, CURRENT_YEAR), maxCal);
    });
  }, [planningHorizonYears]);

  // Reload helpers (live data from Supabase)
  const reloadEntities = useCallback(async () => { if (uid) setEntities(await entitiesService.getAll(uid)); }, [uid]);
  const reloadAssets = useCallback(async () => { if (uid) setAssets(await assetsService.getAll(uid)); }, [uid]);
  const reloadDebts = useCallback(async () => { if (uid) setDebts(await debtsService.getAll(uid)); }, [uid]);
  const reloadRevenues = useCallback(async () => { if (uid) setRevenues(await revenuesService.getAll(uid)); }, [uid]);
  const reloadEvents = useCallback(async () => { if (uid) setPlanEvents(await planEventsService.getAll(uid)); }, [uid]);
  const reloadScenarios = useCallback(async () => { if (uid) setScenarios(await scenariosService.getAll(uid)); }, [uid]);

  // Load data on mount
  useEffect(() => {
    if (!uid) return;
    const load = async () => {
      setLoading(true);
      // Try migration from localStorage → Supabase
      await migrateLocalStorageToSupabase(uid);
      const supaOk = await isSupabaseReady();
      setStorageMode(supaOk ? 'supabase' : 'localStorage');

      const [e, a, d, r, p] = await Promise.all([
        ensureDefaultEntities(uid),
        ensureDefaultAssets(uid),
        debtsService.getAll(uid),
        revenuesService.getAll(uid),
        planEventsService.getAll(uid),
      ]);
      setEntities(e);
      setAssets(a);
      setDebts(d);
      setRevenues(r);
      setPlanEvents(p);

      // Load scenarios
      const sc = await scenariosService.getAll(uid);
      setScenarios(sc);

      setLoading(false);

      // Load properties from Supabase (existing app data)
      const result = await propertyService.getAll(uid);
      if (result?.success && result?.data) setProperties(result.data);
    };
    load();
  }, [uid]);

  // Entity CRUD
  const handleAddEntity = async (entity) => {
    if (activeScenarioId) { await updateActiveScenario('entities', 'add', entity); return; }
    await entitiesService.add(uid, entity); await reloadEntities();
  };
  const handleUpdateEntity = async (id, updates) => {
    if (activeScenarioId) { await updateActiveScenario('entities', 'update', updates, id); return; }
    await entitiesService.update(uid, id, updates); await reloadEntities();
  };
  const handleDeleteEntity = async (id) => {
    if (activeScenarioId) { await updateActiveScenario('entities', 'delete', null, id); return; }
    await entitiesService.remove(uid, id); await reloadEntities();
  };

  // Asset CRUD
  const handleAddAsset = async (asset) => {
    if (activeScenarioId) { return await updateActiveScenario('assets', 'add', asset); }
    const created = await assetsService.add(uid, asset);
    await reloadAssets();
    return created;
  };
  const handleUpdateAsset = async (id, updates) => {
    if (activeScenarioId) { await updateActiveScenario('assets', 'update', updates, id); return; }
    await assetsService.update(uid, id, updates); await reloadAssets();
  };
  const handleDeleteAsset = async (id) => {
    if (activeScenarioId) { await updateActiveScenario('assets', 'delete', null, id); return; }
    await assetsService.remove(uid, id); await reloadAssets();
  };

  // Debt CRUD
  const handleAddDebt = async (debt) => {
    if (activeScenarioId) { await updateActiveScenario('debts', 'add', debt); return; }
    await debtsService.add(uid, debt); await reloadDebts();
  };
  const handleUpdateDebt = async (id, updates) => {
    if (activeScenarioId) { await updateActiveScenario('debts', 'update', updates, id); return; }
    await debtsService.update(uid, id, updates); await reloadDebts();
  };
  const handleDeleteDebt = async (id) => {
    if (activeScenarioId) { await updateActiveScenario('debts', 'delete', null, id); return; }
    await debtsService.remove(uid, id); await reloadDebts();
  };

  // Revenue CRUD
  const handleAddRevenue = async (revenue) => {
    if (activeScenarioId) { await updateActiveScenario('revenues', 'add', revenue); return; }
    await revenuesService.add(uid, revenue); await reloadRevenues();
  };
  const handleUpdateRevenue = async (id, updates) => {
    if (activeScenarioId) { await updateActiveScenario('revenues', 'update', updates, id); return; }
    await revenuesService.update(uid, id, updates); await reloadRevenues();
  };
  const handleDeleteRevenue = async (id) => {
    if (activeScenarioId) { await updateActiveScenario('revenues', 'delete', null, id); return; }
    await revenuesService.remove(uid, id); await reloadRevenues();
  };

  // PlanEvent CRUD
  const handleAddEvent = async (event) => {
    if (activeScenarioId) { updateActiveScenario('events', 'add', event); return; }
    await planEventsService.add(uid, event); await reloadEvents();
  };
  const handleUpdateEvent = async (id, updates) => {
    if (activeScenarioId) { updateActiveScenario('events', 'update', updates, id); return; }
    await planEventsService.update(uid, id, updates); await reloadEvents();
  };
  const handleDeleteEvent = async (id) => {
    if (activeScenarioId) { updateActiveScenario('events', 'delete', null, id); return; }
    await planEventsService.remove(uid, id); await reloadEvents();
  };

  // ─── Cashflow direction (initial) ────────────────────────────────
  const initialCashflowEvent = planEvents.find(e => e.type === 'cashflow_direction' && e.details?.isInitial);
  const handleSaveInitialCashflowDirection = async ({ routingBySource }) => {
    const details = { isInitial: true, routingBySource };
    if (initialCashflowEvent) {
      await handleUpdateEvent(initialCashflowEvent.id, {
        type: 'cashflow_direction',
        name: 'Direction du cashflow initiale',
        year: CURRENT_YEAR,
        details,
      });
      return;
    }

    await handleAddEvent({
      type: 'cashflow_direction',
      name: 'Direction du cashflow initiale',
      year: CURRENT_YEAR,
      notes: '',
      details,
    });
  };

  // ─── Scenario logic ──────────────────────────────────────────────────

  // Helper: generate unique id for scenario items
  const genId = () => crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Update in-memory scenario data and save to storage
  const updateActiveScenario = async (collection, action, data, id) => {
    const scenario = scenarios.find(s => s.id === activeScenarioId);
    if (!scenario) return;
    const items = [...(scenario.data?.[collection] || [])];
    let updated;
    switch (action) {
      case 'add': {
        const newItem = { ...data, id: genId(), createdAt: new Date().toISOString() };
        items.push(newItem);
        updated = newItem;
        break;
      }
      case 'update': {
        const idx = items.findIndex(i => i.id === id);
        if (idx !== -1) items[idx] = { ...items[idx], ...data };
        break;
      }
      case 'delete':
        items.splice(items.findIndex(i => i.id === id), 1);
        break;
    }
    const newData = { ...scenario.data, [collection]: items };
    const newScenario = { ...scenario, data: newData };
    // Update local state
    setScenarios(prev => prev.map(s => s.id === activeScenarioId ? newScenario : s));
    // Update displayed data
    setEntities(newData.entities || []);
    setAssets(newData.assets || []);
    setDebts(newData.debts || []);
    setRevenues(newData.revenues || []);
    setPlanEvents(newData.events || []);
    // Persist
    await scenariosService.update(uid, activeScenarioId, { data: newData });
    return updated;
  };

  // Save current plan as a new scenario
  const handleSaveAsScenario = async (name, description) => {
    const snapshot = {
      entities: JSON.parse(JSON.stringify(activeScenarioId ? entities : entities)),
      assets: JSON.parse(JSON.stringify(assets)),
      debts: JSON.parse(JSON.stringify(debts)),
      revenues: JSON.parse(JSON.stringify(revenues)),
      events: JSON.parse(JSON.stringify(planEvents)),
    };
    await scenariosService.add(uid, { name, description, data: snapshot });
    await reloadScenarios();
  };

  // Load a scenario (switch to scenario mode)
  const handleLoadScenario = (scenarioId) => {
    const scenario = scenarios.find(s => s.id === scenarioId);
    if (!scenario?.data) return;
    setActiveScenarioId(scenarioId);
    setEntities(scenario.data.entities || []);
    setAssets(scenario.data.assets || []);
    setDebts(scenario.data.debts || []);
    setRevenues(scenario.data.revenues || []);
    setPlanEvents(scenario.data.events || []);
  };

  // Go back to live Supabase data
  const handleBackToLive = async () => {
    setActiveScenarioId(null);
    setLoading(true);
    const [e, a, d, r, p] = await Promise.all([
      entitiesService.getAll(uid),
      assetsService.getAll(uid),
      debtsService.getAll(uid),
      revenuesService.getAll(uid),
      planEventsService.getAll(uid),
    ]);
    setEntities(e); setAssets(a); setDebts(d); setRevenues(r); setPlanEvents(p);
    setLoading(false);
  };

  // Duplicate a scenario
  const handleDuplicateScenario = async (scenarioId) => {
    const original = scenarios.find(s => s.id === scenarioId);
    if (!original) return;
    const copy = {
      name: `${original.name} (copie)`,
      description: original.description || '',
      data: JSON.parse(JSON.stringify(original.data)),
    };
    await scenariosService.add(uid, copy);
    await reloadScenarios();
  };

  // Rename a scenario
  const handleRenameScenario = async (scenarioId, newName) => {
    await scenariosService.update(uid, scenarioId, { name: newName });
    await reloadScenarios();
  };

  // Delete a scenario
  const handleDeleteScenario = async (scenarioId) => {
    if (activeScenarioId === scenarioId) {
      await handleBackToLive();
    }
    await scenariosService.remove(uid, scenarioId);
    await reloadScenarios();
  };

  const tabs = [
    { id: 'bilan', label: 'Bilan actuel', icon: 'Wallet' },
    { id: 'revenus', label: 'Revenus', icon: 'Banknote' },
    { id: 'planning', label: 'Planification', icon: 'TrendingUp' },
    { id: 'analyse', label: 'Analyse', icon: 'BarChart3' },
  ];

  if (!user?.id) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationHeader />
        <div className="max-w-5xl mx-auto p-6" style={{ paddingTop: 'calc(64px + 2rem)' }}>
          <div className="rounded-2xl border border-border bg-card p-6 text-center">
            <p className="text-lg font-semibold text-foreground">Connexion requise</p>
            <p className="text-muted-foreground mt-1">Connectez-vous pour acceder a la planification d'investissement.</p>
            <Button className="mt-4" onClick={() => navigate('/login')}>Se connecter</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationHeader subscriptionTier={currentTier} />
        <SubscriptionGate
          requiredTier="pro"
          featureName="l'outil de planification d'investissement"
          onUpgrade={() => navigate('/subscription-pricing')}
          onClose={() => navigate('/main-dashboard')}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="investment-planning-page">
      <NavigationHeader subscriptionTier={currentTier} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{ paddingTop: 'calc(64px + 2rem)' }}>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Button variant="ghost" size="icon" iconName="ArrowLeft" onClick={() => navigate('/main-dashboard')} data-testid="back-to-dashboard" />
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'var(--font-heading)' }}>
                Planification d'investissement
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                Bilan financier complet et projection sur plusieurs années
              </p>
              {storageMode && (
                <p className="text-xs mt-1" style={{ color: storageMode === 'supabase' ? 'var(--color-success)' : 'var(--color-warning)' }}>
                  <Icon name={storageMode === 'supabase' ? 'Cloud' : 'HardDrive'} size={12} className="inline mr-1" />
                  {storageMode === 'supabase' ? 'Synchronisé (Supabase)' : 'Stockage local'}
                </p>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-border" data-testid="planning-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative"
                style={{
                  color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                  fontFamily: 'var(--font-caption)',
                }}
              >
                <Icon name={tab.icon} size={16} />
                {tab.label}
                {activeTab === tab.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center space-y-3">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm text-muted-foreground">Chargement des données...</p>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'bilan' && (
          <div className="space-y-6" data-testid="bilan-tab-content">
            <BilanSection
              id="summary"
              title="Sommaire financier"
              icon="PieChart"
              collapsed={bilanCollapsed.summary}
              onToggle={() => setBilanCollapsed(s => ({ ...s, summary: !s.summary }))}
            >
              <BilanSummary assets={assets} debts={debts} revenues={revenues} entities={entities} formatCurrency={formatCurrency} />
            </BilanSection>

            <BilanSection
              id="cashflow"
              title="Direction du cashflow (initiale)"
              icon="Activity"
              collapsed={bilanCollapsed.cashflow}
              onToggle={() => setBilanCollapsed(s => ({ ...s, cashflow: !s.cashflow }))}
            >
              <CashflowDirectionInitial
                initialEvent={initialCashflowEvent}
                onSave={handleSaveInitialCashflowDirection}
                assets={assets}
                entities={entities}
              />
            </BilanSection>

            <BilanSection
              id="entities"
              title="Détenteurs"
              icon="Users"
              collapsed={bilanCollapsed.entities}
              onToggle={() => setBilanCollapsed(s => ({ ...s, entities: !s.entities }))}
            >
              <EntitiesManager
                entities={entities}
                onAdd={handleAddEntity}
                onUpdate={handleUpdateEntity}
                onDelete={handleDeleteEntity}
              />
            </BilanSection>

            <BilanSection
              id="assets"
              title="Actifs"
              icon="Layers"
              collapsed={bilanCollapsed.assets}
              onToggle={() => setBilanCollapsed(s => ({ ...s, assets: !s.assets }))}
            >
              <AssetsManager
                assets={assets}
                debts={debts}
                entities={entities}
                properties={properties}
                onAdd={handleAddAsset}
                onUpdate={handleUpdateAsset}
                onDelete={handleDeleteAsset}
                onAddDebt={handleAddDebt}
                onAddRevenue={handleAddRevenue}
                formatCurrency={formatCurrency}
              />
            </BilanSection>

            <BilanSection
              id="debts"
              title="Dettes"
              icon="TrendingDown"
              collapsed={bilanCollapsed.debts}
              onToggle={() => setBilanCollapsed(s => ({ ...s, debts: !s.debts }))}
            >
              <DebtsManager
                debts={debts}
                entities={entities}
                assets={assets}
                onAdd={handleAddDebt}
                onUpdate={handleUpdateDebt}
                onDelete={handleDeleteDebt}
                formatCurrency={formatCurrency}
              />
            </BilanSection>

            <BilanSection
              id="revenues"
              title="Revenus locatifs"
              icon="Wallet"
              collapsed={bilanCollapsed.revenues}
              onToggle={() => setBilanCollapsed(s => ({ ...s, revenues: !s.revenues }))}
            >
              <RevenuesManager
                mode="immobilier"
                revenues={revenues}
                entities={entities}
                assets={assets}
                onAdd={handleAddRevenue}
                onUpdate={handleUpdateRevenue}
                onDelete={handleDeleteRevenue}
                formatCurrency={formatCurrency}
              />
            </BilanSection>
          </div>
        )}

        {activeTab === 'revenus' && (
          <RevenuesPlanningTab
            revenues={revenues}
            entities={entities}
            assets={assets}
            onAdd={handleAddRevenue}
            onUpdate={handleUpdateRevenue}
            onDelete={handleDeleteRevenue}
            formatCurrency={formatCurrency}
            currentYear={CURRENT_YEAR}
            horizonYears={planningHorizonYears}
          />
        )}

        {activeTab === 'planning' && (
          <div className="space-y-6" data-testid="planning-tab-content">
            <PlanningHorizonControls
              currentYear={CURRENT_YEAR}
              horizonYears={planningHorizonYears}
              deathYear={planningDeathYear}
              onHorizonChange={setPlanningHorizonYears}
              onDeathYearChange={setPlanningDeathYear}
            />
            {(() => {
              const pRows = planningProjection?.data || [];
              const last = pRows[pRows.length - 1] || {};
              const first = pRows[0] || {};
              const years = Math.max(1, (last.year || CURRENT_YEAR) - (first.year || CURRENT_YEAR));
              const growth = first.netWorth > 0 ? ((Math.pow((last.netWorth || 0) / first.netWorth, 1 / years) - 1) * 100) : 0;
              const estateTax = planningProjection?.deathSummary?.totalTaxAtDeath || 0;
              const kpis = [
                { label: "Années de projection", value: `${planningHorizonYears} ans`, icon: 'CalendarDays' },
                { label: 'Valeur projetée', value: formatCurrency(last.netWorth || 0), icon: 'Wallet' },
                { label: 'Impôt successoral', value: `-${formatCurrency(estateTax)}`, icon: 'Receipt' },
                { label: 'Croissance annuelle', value: `${Number.isFinite(growth) ? growth.toFixed(1) : '0.0'}%`, icon: 'TrendingUp' },
              ];
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                  {kpis.map((k) => (
                    <div key={k.label} className="rounded-xl border border-border bg-card px-4 py-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name={k.icon} size={14} className="text-primary" />
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{k.label}</p>
                      </div>
                      <p className="text-xl font-semibold text-foreground" style={{ fontFamily: 'var(--font-data)' }}>{k.value}</p>
                    </div>
                  ))}
                </div>
              );
            })()}

            <div className="w-full rounded-xl border border-border bg-card p-4 md:p-5">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="GitBranch" size={18} className="text-primary shrink-0" />
                <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'var(--font-heading)' }}>
                  Scénario actif
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Sélection du scénario</label>
                  <select
                    value={activeScenarioId || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) handleBackToLive();
                      else handleLoadScenario(v);
                    }}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                  >
                    <option value="">Données en direct</option>
                    {scenarios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => {
                    if (!activeScenarioId && scenarios[0]?.id) handleLoadScenario(scenarios[0].id);
                    if (activeScenarioId) handleLoadScenario(activeScenarioId);
                  }}
                >
                  Charger
                </Button>
                <Button
                  variant="outline"
                  iconName="Copy"
                  iconPosition="left"
                  onClick={() => {
                    if (activeScenarioId) handleDuplicateScenario(activeScenarioId);
                  }}
                  disabled={!activeScenarioId}
                >
                  Dupliquer
                </Button>
              </div>
            </div>

            {(() => {
              const displayEvents = planEvents.filter(e => !(e.type === 'cashflow_direction' && e.details?.isInitial));
              return (
                <>
                  <PlanningEvents
                    events={displayEvents}
                    entities={entities}
                    assets={assets}
                    debts={debts}
                    properties={properties}
                    projectionData={planningProjection.data}
                    planningHorizonYears={planningHorizonYears}
                    onAdd={handleAddEvent}
                    onUpdate={handleUpdateEvent}
                    onDelete={handleDeleteEvent}
                    formatCurrency={formatCurrency}
                  />
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'analyse' && (
          <AnalysisTab
            scenarioAId={analysisScenarioA}
            scenarioBId={analysisScenarioB}
            onScenarioAChange={setAnalysisScenarioA}
            onScenarioBChange={setAnalysisScenarioB}
            projectionA={analysisProjectionA}
            projectionB={analysisProjectionB}
            scenarios={scenarios}
            activeScenarioId={activeScenarioId}
            planningHorizonYears={planningHorizonYears}
            planningDeathYear={planningDeathYear}
            onHorizonChange={setPlanningHorizonYears}
            onDeathYearChange={setPlanningDeathYear}
            currentYear={CURRENT_YEAR}
            analysisAssetsA={analysisDebugAssetsA}
            analysisAssetsB={analysisDebugAssetsB}
            analysisEntitiesA={analysisEntitiesA}
            analysisEntitiesB={analysisEntitiesB}
            formatCurrency={formatCurrency}
            onGoToPlanning={() => setActiveTab('planning')}
          />
        )}
          </>
        )}
      </main>
    </div>
  );
};

export default InvestmentPlanning;
